'use client';

import {
  CollapsibleContentSlot,
  CollapsibleTooltipContentSlot,
  CollapsibleTooltipSlot,
  CollapsibleTriggerSlot,
} from '@magicschool/spellbook/components/Collapsible/slots';
import { useSlots } from '@magicschool/spellbook/hooks/useSlots';
import { cn } from '@magicschool/spellbook/lib/utils';
import * as CollapsiblePrimitive from '@radix-ui/react-collapsible';

const CollapsibleBase = ({ children, className, ...props }: React.ComponentProps<typeof CollapsiblePrimitive.Root>) => {
  const { slots } = useSlots(children, {
    trigger: { Component: CollapsibleTriggerSlot },
    content: { Component: CollapsibleContentSlot },
    tooltip: { Component: CollapsibleTooltipSlot },
  });

  return (
    <CollapsiblePrimitive.Root className={cn('w-full', className)} data-spellbook-name="collapsible" {...props}>
      {slots.tooltip ?? slots.trigger}
      {slots.content}
    </CollapsiblePrimitive.Root>
  );
};

/**
 * A single expand/collapse section with animated height transitions. Built on Radix Collapsible for accessible disclosure of supplementary content.
 *
 * ### When to use
 * - To progressively reveal content that isn't critical on first view (e.g., advanced options, additional details)
 * - When you need a single toggle to show/hide a section with a smooth height animation
 * - As a building block for custom disclosure patterns not covered by existing components
 *
 * ### When NOT to use
 * - Several Spellbook components already provide collapsible or disclosure behavior. Prefer these when they fit your use case:
 *   - `Accordion`: multiple collapsible sections with mutual exclusion, styled headers, and icon indicators
 *   - `NavLink`: navigation item with built-in collapsible sub-links
 * - Only reach for `Collapsible` directly when you need a single expand/collapse region with full control over trigger and content styling
 *
 * ### API
 * - `open` / `onOpenChange`: Controlled open state. Omit for uncontrolled behavior with `defaultOpen`.
 * - `defaultOpen`: Uncontrolled initial open state. Default: `false`
 * - `disabled`: When `true`, prevents the trigger from toggling the content
 *
 * ### Composition (slots)
 * - `<Collapsible.Trigger>`: The element that toggles the content. Set `asChild` when passing a focusable element like a `Button` so it becomes the trigger directly. Must be included explicitly.
 * - `<Collapsible.Content>`: The region that animates open and closed. Uses `animate-collapsible-open` / `animate-collapsible-close` for height transitions. Must be included explicitly.
 * - `<Collapsible.Tooltip>`: Optional wrapper that adds a hover tooltip to the trigger. Nest `Collapsible.Trigger` and `Collapsible.TooltipContent` inside it. Replaces the trigger slot when present.
 * - `<Collapsible.TooltipContent>`: Text content for the tooltip. Renders as a `Text` caption. Use as a child of `Collapsible.Tooltip`.
 *
 * ### Visual states
 * - **Collapsed** (default): Content is hidden with zero height. Only the trigger is visible.
 * - **Expanded**: Content animates to full height. The trigger remains visible above or beside the content.
 * - **Disabled**: Trigger is non-interactive. Content stays in its current state (open or closed).
 *
 * ### Accessibility
 * - Radix sets `aria-expanded` on the trigger and `aria-controls` pointing to the content region automatically
 * - Use `asChild` on the trigger when passing a `Button` or other focusable element to avoid nested focusable elements
 * - The content region is hidden from the accessibility tree when collapsed
 */
export const Collapsible = Object.assign(CollapsibleBase, {
  Trigger: CollapsibleTriggerSlot,
  Content: CollapsibleContentSlot,
  Tooltip: CollapsibleTooltipSlot,
  TooltipContent: CollapsibleTooltipContentSlot,
});

export { CollapsiblePrimitive };
